import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import { TableHeadComponent } from "../../common/TableHead";
import { Autocomplete, TextField } from "@mui/material";
import { createCameraAlarmType } from "./EditCameraUtils/createCameraAlarmType";
import { getNumbersAfterEditCamera } from "./EditCamera";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";
import { CameraAudioSetting } from "./CameraAudioSetting";
import { CameraMotionSetting } from "./CameraMotionSetting";
import { checkedCircleIcon } from "../../common/checkedCircleIcon";
import { cancelIconRed } from "../../common/cancelIconRed";
import { useAppStore } from "../../../appStore";
import {
  GENERAL_INFO_COLUMN_COUNT,
  connectionFieldTextFieldSx,
  generalInfoTableCellSx,
} from "./EditCameraUtils/editCameraGeneralInfoLayout";
import {
  GeneralInfoConnectionEditRows,
  GeneralInfoConnectionHeaderRows,
} from "./GeneralInfoConnectionTableRows";

export const GeneralInfoEditCameraTabEditMode = ({
  siteInfo,
  cameraGeneralInfo,
  setCameraGeneralInfo,
  setPatchObject,
  setGeneralInfoChangesMade,
  setActiveChangeMade,
}: {
  siteInfo: any;
  cameraGeneralInfo: any;
  setCameraGeneralInfo: any;
  setPatchObject: any;
  setGeneralInfoChangesMade: any;
  setActiveChangeMade: any;
}) => {
  const { userPermissions } = useAppStore();
  const cameraViewOptions = JSON.parse(localStorage.cameraViewOptions);

  const cameraViewOptionIds = new Map();
  const cameraViewOptionNames = new Map();
  cameraViewOptions.forEach((option: any) => {
    cameraViewOptionIds.set(option.name, option.value);
  });
  cameraViewOptions.forEach((option: any) => {
    cameraViewOptionNames.set(option.value, option.name);
  });

  const connectionsTitlesArray = JSON.parse(
    localStorage.connectionsTitlesArray
  );

  const [showPassword, setShowPassword] = React.useState(false);

  // The camera's own login as loaded, before any typing (ENG-2144). A blank
  // value means the camera uses the site's credentials.
  const initialCredentials = React.useRef({
    username: cameraGeneralInfo.username ?? "",
    password: cameraGeneralInfo.password ?? "",
  });

  /**
   * Record a username/password edit. When the camera had no login of its own
   * and the field is blank again, drop the key from the PATCH instead of
   * sending "" over the site-inherited credential. A camera that had its own
   * value keeps today's behaviour: clearing it sends "".
   */
  const handleCredentialChange = (
    field: "username" | "password",
    nextValue: string
  ) => {
    setCameraGeneralInfo((previousState: any) => ({
      ...previousState,
      [field]: nextValue,
    }));
    setPatchObject((previousState: any) => {
      if (!nextValue && !initialCredentials.current[field]) {
        const rest = { ...previousState };
        delete rest[field];
        return rest;
      }
      return { ...previousState, [field]: nextValue };
    });
    setGeneralInfoChangesMade(true);
  };

  const [livestreamUrlError, setLivestreamUrlError] = React.useState(false);

  const { chmActive, notesText } = useEditCameraStore();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const [titlesArray, setTitlesArray] = React.useState(["", ""]);

  const autopatrolSite = siteInfo.integration_type_name === "autopatrol";

  React.useEffect(() => {
    if (cameraGeneralInfo.streams[0].show_nvr_alarm) {
      setTitlesArray(["SES recipients", "NVR Alarm"]);
    } else {
      setTitlesArray(["SES recipients", ""]);
    }
  }, []);

  const generalInfoHeaders = autopatrolSite
    ? ["Customer", "Camera Name", "Active", "Notes"]
    : [
        "Customer",
        "Camera Name",
        "Camera View",
        "Alarm Type",
        "Active",
        "Camera Health Monitoring",
        "Notes",
      ];

  return (
    <TableContainer component={Paper}>
      <Table
        sx={{ minWidth: 1000, fontFamily: "Mulish", tableLayout: "fixed" }}
        aria-label="simple table"
      >
        <colgroup>
          {Array.from({ length: GENERAL_INFO_COLUMN_COUNT }).map((_, index) => (
            <col
              key={`general-info-col-${index}`}
              style={{ width: `${100 / GENERAL_INFO_COLUMN_COUNT}%` }}
            />
          ))}
        </colgroup>
        <TableHeadComponent titlesArray={generalInfoHeaders} />
        <TableBody>
          <TableRow key={getNumbersAfterEditCamera(location.href)}>
            <TableCell align="left" sx={generalInfoTableCellSx}>
              {siteInfo.parent_group.name}
            </TableCell>
            {/* <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
              {cameraGeneralInfo.camera_name}
            </TableCell> */}
            <TableCell align="left" sx={generalInfoTableCellSx}>
              <div key={siteInfo.name}>
                <TextField
                  defaultValue={cameraGeneralInfo.camera_name}
                  variant="standard"
                  sx={connectionFieldTextFieldSx}
                  type="string"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setCameraGeneralInfo((previousState: any) => {
                      return {
                        ...previousState,
                        camera_name: event.target.value,
                      };
                    });
                    setPatchObject((previousState: any) => {
                      return {
                        ...previousState,
                        camera_name: event.target.value,
                      };
                    });
                    setGeneralInfoChangesMade(true);
                  }}
                />
              </div>
            </TableCell>
            {!autopatrolSite && (
              <TableCell align="left" sx={generalInfoTableCellSx}>
                <Autocomplete
                  disablePortal
                  options={cameraViewOptions.map((option: any) => {
                    return option.name;
                  })}
                  defaultValue={cameraViewOptionNames.get(
                    cameraGeneralInfo.camera_view
                  )}
                  onChange={(event: any, value: any) => {
                    setCameraGeneralInfo((previousState: any) => {
                      return {
                        ...previousState,
                        camera_view: cameraViewOptionIds.get(value),
                      };
                    });
                    setPatchObject((previousState: any) => {
                      return {
                        ...previousState,
                        camera_view: cameraViewOptionIds.get(value),
                      };
                    });
                    setGeneralInfoChangesMade(true);
                  }}
                  sx={connectionFieldTextFieldSx}
                  renderInput={(params) => (
                    <TextField {...params} variant="standard" sx={{}} />
                  )}
                ></Autocomplete>
              </TableCell>
            )}
            {!autopatrolSite && (
              <TableCell align="left" sx={generalInfoTableCellSx}>
                {createCameraAlarmType(cameraGeneralInfo.default_alarm)}
              </TableCell>
            )}
            {!autopatrolSite && (
              <TableCell align="left" sx={generalInfoTableCellSx}>
                {cameraGeneralInfo.active ? (
                  <Switch
                    defaultChecked
                    color="warning"
                    disabled={false}
                    onChange={(event: any) => {
                      setCameraGeneralInfo((previousState: any) => {
                        return {
                          ...previousState,
                          active: event.target.checked,
                        };
                      });
                      setActiveChangeMade(true);
                    }}
                  />
                ) : (
                  <Switch
                    color="warning"
                    disabled={false}
                    onChange={(event: any) => {
                      setCameraGeneralInfo((previousState: any) => {
                        return {
                          ...previousState,
                          active: event.target.checked,
                        };
                      });
                      setActiveChangeMade(true);
                    }}
                  />
                )}
              </TableCell>
            )}
            {siteInfo.integration_type_name === "autopatrol" && (
              <TableCell align="left" sx={generalInfoTableCellSx}>
                {chmActive ? checkedCircleIcon : cancelIconRed}
              </TableCell>
            )}
            {!autopatrolSite && (
              <TableCell align="left" sx={generalInfoTableCellSx}>
                {chmActive ? checkedCircleIcon : cancelIconRed}
              </TableCell>
            )}
            <TableCell align="left" sx={generalInfoTableCellSx}>
              {/* <OrangeTextButton
                onClick={() => {
                  setNotesDialogEditCameraOpen(true);
                }}
                text="See Notes"
                disabled={false}
              /> */}
              <TextField
                defaultValue={notesText}
                variant="outlined"
                multiline
                rows={4}
                sx={{ width: 225 }}
                type="string"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPatchObject((previousState: any) => {
                    return {
                      ...previousState,
                      notes: event.target.value,
                    };
                  });
                  setGeneralInfoChangesMade(true);
                }}
              />
            </TableCell>
          </TableRow>
          {!autopatrolSite && (
            <>
              <GeneralInfoConnectionHeaderRows
                connectionTitles={connectionsTitlesArray}
              />
              <GeneralInfoConnectionEditRows
                siteInfo={siteInfo}
                cameraGeneralInfo={cameraGeneralInfo}
                setCameraGeneralInfo={setCameraGeneralInfo}
                setPatchObject={setPatchObject}
                setGeneralInfoChangesMade={setGeneralInfoChangesMade}
                userPermissions={userPermissions}
                showPassword={showPassword}
                onToggleShowPassword={handleClickShowPassword}
                onMouseDownPassword={handleMouseDownPassword}
                onMouseUpPassword={handleMouseUpPassword}
                handleCredentialChange={handleCredentialChange}
                livestreamUrlError={livestreamUrlError}
                setLivestreamUrlError={setLivestreamUrlError}
              />
            </>
          )}
        </TableBody>
      </Table>
      {/* Camera audio / listen-in (ENG-2204): sits under the connections
          rows, saves on its own, and gates itself (flag + staff + RTSP). */}
      <CameraAudioSetting
        cameraGeneralInfo={cameraGeneralInfo}
        integrationTypeName={siteInfo?.integration_type_name}
      />
      {!autopatrolSite && (
        <Table
          sx={{ minWidth: 1000, fontFamily: "Mulish" }}
          aria-label="simple table"
        >
          <TableHeadComponent titlesArray={titlesArray} />
          <TableBody>
            <TableRow key={getNumbersAfterEditCamera(location.href)}>
              <TableCell align="left" sx={{ fontFamily: "Mulish", width: 600 }}>
                <div key="ses_recipients">
                  <TextField
                    multiline
                    id="ses_recipients"
                    label="Recipient emails"
                    name="ses_recipients"
                    defaultValue={cameraGeneralInfo.streams[0].ses_recipients}
                    sx={{ fontFamily: "Mulish", width: 600 }}
                    helperText="E-mails to receive notifications with images attached. You can specify more than one separated by comma."
                    rows={2}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newStreams = cameraGeneralInfo.streams;
                      newStreams.forEach((object: any) => {
                        object.ses_recipients = event.target.value;
                      });
                      setPatchObject((previousState: any) => {
                        return {
                          ...previousState,
                          streams: newStreams,
                        };
                      });
                      setCameraGeneralInfo((previousState: any) => {
                        return {
                          ...previousState,
                          streams: newStreams,
                        };
                      });
                      setGeneralInfoChangesMade(true);
                    }}
                  />
                </div>
              </TableCell>
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", zIndex: "modal" }}
              >
                {cameraGeneralInfo.streams[0].show_nvr_alarm && (
                  <div>
                    {cameraGeneralInfo.streams[0].nvr_alarm ? (
                      <Switch
                        sx={{ top: -40 }}
                        defaultChecked
                        color="warning"
                        disabled={false}
                        onChange={(event: any) => {
                          const newStreams = cameraGeneralInfo.streams;
                          newStreams.forEach((object: any) => {
                            object.nvr_alarm = event.target.checked;
                          });
                          setCameraGeneralInfo((previousState: any) => {
                            return {
                              ...previousState,
                              streams: newStreams,
                            };
                          });
                          setPatchObject((previousState: any) => {
                            return {
                              ...previousState,
                              streams: newStreams,
                            };
                          });
                          setGeneralInfoChangesMade(true);
                        }}
                      />
                    ) : (
                      <Switch
                        sx={{ top: -40 }}
                        color="warning"
                        disabled={false}
                        onChange={(event: any) => {
                          const newStreams = cameraGeneralInfo.streams;
                          newStreams.forEach((object: any) => {
                            object.nvr_alarm = event.target.checked;
                          });
                          setCameraGeneralInfo((previousState: any) => {
                            return {
                              ...previousState,
                              streams: newStreams,
                            };
                          });
                          setPatchObject((previousState: any) => {
                            return {
                              ...previousState,
                              streams: newStreams,
                            };
                          });
                          setGeneralInfoChangesMade(true);
                        }}
                      />
                    )}
                  </div>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
      {/* Per-camera motion (ENG-2242): saves on its own and hides itself
          where the setting means nothing (SMTP, AutoPatrol, …). */}
      <CameraMotionSetting
        cameraGeneralInfo={cameraGeneralInfo}
        siteInfo={siteInfo}
      />
    </TableContainer>
  );
};
